import { format, isValid, parseISO } from 'date-fns'

import {
  getStatus,
  toCoverLocaleString,
  toLocaleString,
  toLocaleString2,
  toLocaleString3
} from '@helper/grid/strings'
import { parseCurrency } from '@helper/parseCurrency'

import {
  IMrpRecomendacaoItem,
  IPendingOrderFornecedor,
  IPendingOrderItem
} from '@base/@types/providers/Mrp/ApprovalProvider'

// Tipo de processo SAP da compra programada
export const TIPO_PROCESSO_COMPRA_PROGRAMADA = '108'

// Converte string ISO (ou Date) em Date; parseISO evita o deslocamento de dia
// que `new Date('yyyy-MM-dd')` causa ao interpretar a data como UTC
export const toDate = (value?: string | Date | null): Date | null => {
  if (!value) return null
  const date = value instanceof Date ? value : parseISO(value)
  return isValid(date) ? date : null
}

// Datas nulas/inválidas viram '-' (nunca 01/01/1970)
export const formatDate = (
  value?: string | Date | null,
  pattern = 'dd/MM/yyyy HH:mm'
): string => {
  const date = toDate(value)
  return date ? format(date, pattern) : '-'
}

export const getShortPedidoId = (fullId?: string | null): string =>
  (fullId || '').split('-')[0]

export const isScheduledPurchase = (
  tipoProcesso?: string | number | null
): boolean => String(tipoProcesso ?? '') === TIPO_PROCESSO_COMPRA_PROGRAMADA

// Pedido ainda não decidido: o backend pode mandar null ou omitir o campo
export const isPendente = (aprovado?: boolean | null): boolean =>
  aprovado == null

// Status do pedido derivado de todos os itens (não só do primeiro)
export const getGroupAprovado = (
  items: Pick<IPendingOrderItem, 'aprovado'>[]
): boolean | null => {
  if (items.some(item => item.aprovado === false)) return false
  if (items.length > 0 && items.every(item => item.aprovado === true)) {
    return true
  }
  return null
}

// Fornecedores distintos dos itens do pedido (por código; sem código, pelo nome)
export const getGroupFornecedores = (
  items: Pick<IPendingOrderItem, 'codigoFornecedor' | 'nomeFornecedor'>[]
): IPendingOrderFornecedor[] => {
  const fornecedores = new Map<string, IPendingOrderFornecedor>()

  items.forEach(({ codigoFornecedor, nomeFornecedor }) => {
    const nome = nomeFornecedor?.trim() || ''
    if (!codigoFornecedor && !nome) return

    const key = codigoFornecedor ? `c:${codigoFornecedor}` : `n:${nome}`
    if (!fornecedores.has(key)) {
      fornecedores.set(key, { codigo: codigoFornecedor, nome })
    }
  })

  return Array.from(fornecedores.values())
}

// Rótulo do fornecedor para exibição (fallback no código quando sem nome)
export const formatFornecedor = ({
  codigo,
  nome
}: IPendingOrderFornecedor): string => nome || `Fornecedor ${codigo}`

// Detecção única de itens com falha (usada no modal e na aprovação rápida)
export const hasFalha = (item: IPendingOrderItem): boolean =>
  !!(item?.erro || item?.falha || item?.hasError)

export const getItensComFalha = (
  items?: IPendingOrderItem[] | null
): IPendingOrderItem[] => (items || []).filter(hasFalha)

export const getFalhaDescricao = (item: IPendingOrderItem): string =>
  item?.erro || item?.falha || 'Falha não especificada'

// Número digitado/exibido em pt-BR ('T1.234', '1.234', '1.234,5') -> number
// Retorna NaN para entrada vazia ou inválida
export const parsePtBrNumber = (value: unknown): number => {
  if (typeof value === 'number') return value
  const normalized = String(value ?? '')
    .trim()
    .replace(/^[TF]/, '')
    .replace(/\s/g, '')
    .replace(/\./g, '')
    .replace(',', '.')
  if (normalized === '') return NaN
  return Number(normalized)
}

// Quantidade desejada do item MRP bruto (compra programada usa compraProgramaDesejada)
export const getDesiredQuantity = (
  item: IMrpRecomendacaoItem | null | undefined,
  isScheduled: boolean
): number | undefined => {
  const value = isScheduled
    ? item?.compraProgramaDesejada?.totalPedidos ?? item?.desejada?.quantidade
    : item?.desejada?.quantidade
  return typeof value === 'number' ? value : undefined
}

export const getRecommendedQuantity = (
  item: IMrpRecomendacaoItem | null | undefined,
  isScheduled: boolean
): number | undefined => {
  const value = isScheduled
    ? item?.compraProgramaRecomendada?.totalPedidos
    : item?.valorRecomendacao?.quantidade
  return typeof value === 'number' ? value : undefined
}

// Cópia imutável do item com a nova quantidade desejada
export const withDesiredQuantity = (
  item: IMrpRecomendacaoItem,
  quantidade: number,
  isScheduled: boolean
): IMrpRecomendacaoItem =>
  isScheduled
    ? {
        ...item,
        compraProgramaDesejada: {
          ...item.compraProgramaDesejada,
          totalPedidos: quantidade
        }
      }
    : {
        ...item,
        desejada: {
          ...item.desejada,
          quantidade
        }
      }

const formatShortDate = (value?: string | null): string => {
  const date = toDate(value)
  return date ? format(date, 'dd/MM/yyyy') : ''
}

const toInteger = (value: number) =>
  parseCurrency({
    value: value || 0,
    country: 'pt-br',
    minFraction: 0,
    maxFraction: 0
  })

// Parser de compra programada para a grid de MRP da aprovação
export const parsePurchaseMrpScheduledData = (
  data: IMrpRecomendacaoItem[]
): any[] => {
  const res = []

  for (const item of data) {
    const recomendada = item.compraProgramaRecomendada || {}
    const desejada = item.compraProgramaDesejada || {}
    const desejadaData = item.desejada || {}

    res.push({
      'centro-id': item.estoque?.loja ? item.estoque?.loja?.id : 0,
      'centro-nome': item.estoque?.loja ? item.estoque?.loja?.descricao : '',
      'produto-id': item.estoque?.produtoFornecedor
        ? item.estoque?.produtoFornecedor?.produto?.id
        : 0,
      'produto-categoria-compacta':
        item.estoque?.categoriaCompactaDescricao?.toUpperCase() || '',
      'produto-mundo': item.estoque?.mundoDescricao?.toUpperCase() || '',
      'produto-material': item.estoque?.produtoFornecedor
        ? item.estoque?.produtoFornecedor?.produto?.descricao
        : '',
      'produto-categoria': item.estoque?.produtoFornecedor
        ? item.estoque?.produtoFornecedor?.produto?.categoria?.descricao
        : '',
      'produto-curva-abc': item.estoque?.curvaABC || '',
      'produto-curva-abc-empresa': item.estoque?.curvaABCEmpresa || '',
      'produto-status': item.estoque?.textoStatus || '',
      'produto-lote': toLocaleString3(
        item.estoque?.produtoFornecedor?.quantidadePacote || 0
      ),
      'produto-grupo-separacao':
        item.estoque?.produtoFornecedor?.produto?.grupoCarga?.descricao || '',
      'fornecedor-id': item.estoque?.produtoFornecedor?.fornecedor
        ? item.estoque?.produtoFornecedor?.fornecedor?.id
        : 0,
      'fornecedor-nome': item.estoque?.produtoFornecedor?.fornecedor
        ? item.estoque?.produtoFornecedor?.fornecedor?.descricao
        : '',
      'recomendacao-quantidade': toLocaleString2(recomendada.totalPedidos || 0),
      'recomendacao-peso': toLocaleString(recomendada.pesoTotal || 0),
      'recomendacao-volume': toLocaleString(recomendada.volumeTotal || 0),
      'recomendacao-custo': toLocaleString(recomendada.custoTotal || 0),
      'recomendacao-tipo-alerta': getStatus(item.motivoRecomendacao || 0),
      'recomendacao-nivel-servico': (
        desejadaData?.nivelServico * 100 || 0
      ).toLocaleString('pt-BR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      }),
      'recomendacao-inicio': formatShortDate(recomendada.inicio),
      'recomendacao-fim': formatShortDate(recomendada.fim),
      'compra-peso': toLocaleString(desejada.pesoTotal || 0),
      'compra-volume': toLocaleString(desejada.volumeTotal || 0),
      'compra-desejada':
        (item.ajustadoPeloFaturamentoMinimo === true ? 'T' : 'F') +
        toInteger(desejada.totalPedidos),
      'compra-custo': toLocaleString(desejada.custoTotal || 0),
      'compra-custo-unit': toLocaleString(
        item.estoque?.custoUnitarioFatorConversao || 0
      ),
      'compra-nivel-servico': (
        desejadaData?.nivelServico * 100 || 0
      ).toLocaleString('pt-BR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      }),
      'compra-inicio': formatShortDate(desejada.inicio),
      'compra-fim': formatShortDate(desejada.fim),
      'estoque-max': toLocaleString2(item.estoque?.maximo || 0),
      'estoque-cd': toLocaleString2(
        item.estoque?.disponivelReservadoCentroDistribuicao || 0
      ),
      'estoque-reservado': toLocaleString2(
        item.estoque?.quantidadeReservada || 0
      ),
      'estoque-id': item.estoque?.id || 0,
      'estoque-disponivel': toInteger(item.estoque?.disponivel),
      'estoque-minimo': toInteger(item.estoque?.estoqueMinimo),
      'estoque-ordenado': toInteger(item.estoque?.ordenado),
      'estoque-periodo-revisao': item.estoque?.periodoRevisao || 0,
      'estoque-tempo-espera': item.estoque?.tempoDeEspera || 0,
      'estoque-total': toInteger(item.estoque?.total),
      'estoque-total-ordenado': toInteger(item.estoque?.totalMaisOrdenado),
      'simulacao-demanda-media': toLocaleString(
        recomendada.demandaMediaDiaria || 0
      ),
      'simulacao-cobertura-media': toCoverLocaleString(
        recomendada.coberturaMedia || 0
      ),
      'simulacao-numero-pedidos': toLocaleString2(
        recomendada.numeroDePedidos || 0
      ),
      'cobertura-alvo': toCoverLocaleString(item.coberturaAlvo || 0),
      'cobertura-desejada': toCoverLocaleString(item.coberturaDesejada || 0),
      diferenca: item.diferenca || 0,
      'alcancou-recomendacao': item.alcancouRecomendacaoDesejada
        ? 'Sim'
        : 'Não',
      'acima-tolerancia': item.acimaToleranciaPermitida ? 'Sim' : 'Não'
    })
  }

  return res
}
